import { useEffect, useRef } from 'react';

import type { RefObject } from 'react';

const SCROLL_THRESHOLD = 20;

export const useAutoScroll = (bottomRef: RefObject<HTMLDivElement | null>, dependency: unknown) => {
  const hasUserScrolledRef = useRef(false);
  const isAtBottomRef = useRef(true);

  useEffect(() => {
    const bottom = bottomRef.current;
    if (!bottom) {
      return;
    }

    const container = findScrollableParent(bottom);
    if (!container) {
      return;
    }

    hasUserScrolledRef.current = false;
    isAtBottomRef.current = true;

    const onScroll = () => {
      hasUserScrolledRef.current = true;
      isAtBottomRef.current = isScrolledToBottom(container);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [bottomRef]);

  useEffect(() => {
    const shouldScroll = !hasUserScrolledRef.current || isAtBottomRef.current;
    if (shouldScroll && bottomRef.current?.scrollIntoView) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [bottomRef, dependency]);
};

const isScrolledToBottom = (el: HTMLElement) => el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;

const findScrollableParent = (el: HTMLElement): HTMLElement | null => {
  let current = el.parentElement;
  while (current) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};
